import { extractEventIdFromSlug } from "@/app/[locale]/(root)/event/[slug]/utils/eventUtils"
import { HttpClient } from "@/app/api/HttpClient"
import { API_PREFIX_EVENT_PATH } from "@/constants/prefix-path-api"
import httpClient from "@/lib/http-client"
import type { CreateEventData, EventData, EventResponse, SingleEventResponse, UpdateEventData } from "@/types/event"
import { buildUrlWithQuery } from "@/utils/query-string-api-helper"
import { message } from "antd"

const getEvents = async (searchTerms: Record<string, any> = {}): Promise<EventResponse> => {
  const isStatusDraft = searchTerms?.publicationState === "draft"

  const params: any = {
    populate: "*",
    sort: searchTerms.sort || "publishedAt:asc",
    filters: {},
    pagination: {
      pageSize: searchTerms.pageSize || "20",
      withCount: true,
    },
    publicationState: isStatusDraft ? "preview" : (searchTerms?.publicationState ?? "preview"),
  }

  const systemKeys = ["page", "pageSize", "sort", "publicationState", "locale"]

  Object.keys(searchTerms).forEach(key => {
    const value = searchTerms[key]
    if (key === "locale" && value !== "all") {
      params.filters.locale = { $eq: value }
    } else if (key === "domain" && value) {
      params.filters.websites = {
        domain: {
          $containsi: value,
        },
      }
    } else if (key === "page") {
      params.pagination.page = value
    } else if (key === "pageSize") {
      params.pagination.pageSize = value
    } else if (!systemKeys.includes(key) && value && key !== "pagination") {
      params.filters[key] = { $containsi: value }
    }
  })

  if (isStatusDraft) {
    params.filters.publishedAt = { $null: true }
  }

  // Use `http-client.ts` (fetch-based) for GET requests.
  // `buildUrlWithQuery` ensures Strapi query params (nested/arrays) are serialized correctly.
  const response = await httpClient.get<EventResponse>(buildUrlWithQuery("events", params))
  if (!response) {
    throw new Error("Failed to fetch events")
  }

  return response
}

const getEventById = async (id: number, populate: string = "*"): Promise<SingleEventResponse | null> => {
  const res = await HttpClient.get<SingleEventResponse>(`${API_PREFIX_EVENT_PATH}/${id}?populate=${populate}`)
  return res
}

const getEventBySlug = async (slug: string): Promise<EventData> => {
  const eventId = extractEventIdFromSlug(slug)
  if (!eventId) {
    throw new Error("Invalid event slug")
  }
  const response = await getEventById(eventId)
  if (!response || !response.data) {
    throw new Error("Event not found")
  }
  return response.data
}

const createEvent = async (data: CreateEventData): Promise<SingleEventResponse> => {
  const res = await HttpClient.post(`${API_PREFIX_EVENT_PATH}`, { data })
  return res.data
}

const updateEvent = async (id: number, data: UpdateEventData): Promise<SingleEventResponse> => {
  const res = await HttpClient.put(`${API_PREFIX_EVENT_PATH}/${id}`, { data })
  return res.data
}

const deleteEvent = async (id: number): Promise<SingleEventResponse> => {
  const res = await HttpClient.delete(`${API_PREFIX_EVENT_PATH}/${id}`)
  return res.data
}

const bulkDeleteEvents = async (ids: number[]) => {
  try {
    await Promise.all(ids.map(id => deleteEvent(id)))
  } catch (error) {
    console.error("Error deleting events id: ", ids, error)
    throw error
  }
}

const bulkUpdateEventStatus = async (ids: number[], status: "publish" | "draft") => {
  const publishedAt = status === "publish" ? new Date().toISOString() : null
  try {
    await Promise.all(ids.map(id => updateEvent(id, { publishedAt } as any)))
  } catch (error) {
    console.error("Có lỗi khi cập nhật sự kiện:", ids, error)
    throw error
  }
}

const createEventLocalization = async (id: number, data: any) => {
  try {
    const res = await HttpClient.post(`${API_PREFIX_EVENT_PATH}/${id}/localizations`, data)
    return res
  } catch (err) {
    const error = err as any
    message.error(error.response?.data?.error?.message || "Thất bại khi tạo bản dịch!")
    throw err
  }
}

export {
  bulkDeleteEvents,
  bulkUpdateEventStatus,
  createEvent,
  createEventLocalization,
  deleteEvent,
  getEventById,
  getEventBySlug,
  getEvents,
  updateEvent,
}
